const methodCards = [
  {
    icon: 'ri-ancient-gate-line',
    label: 'Ancient Wisdom',
    title: 'Classical Foundation',
    description:
      'Classical Chinese Medicine offers a deep understanding of systemic balance and energetic pathways through which tension and depletion manifest in the body. Meridian theory informs how internal imbalance gradually shapes facial tone and expression.',
  },
  {
    icon: 'ri-microscope-line',
    label: 'Modern Aesthetic Precision',
    title: 'Clinical Observation',
    description:
      'Modern aesthetic assessment allows for refined observation of facial structure, tissue response, and subtle shifts in tone and symmetry. Precision enhances understanding but never replaces foundational regulation.',
  },
  {
    icon: 'ri-plant-line',
    label: 'Botanical Intelligence',
    title: 'Plant-Based Modulation',
    description:
      "Botanical compounds support the body's innate capacity for nervous system modulation and adaptive repair. Plant intelligence bridges internal regulation and visible skin resilience.",
  },
];

const archetypes = [
  {
    id: 1,
    number: '01',
    name: 'The Held Composure',
    tagline: 'Chronic Tension Pattern',
    description:
      'You appear composed — but sustained sympathetic activation creates jaw holding, mid-face rigidity, and compressed expression.',
  },
  {
    id: 2,
    number: '02',
    name: 'The Quiet Depletion',
    tagline: 'Structural Fade Pattern',
    description:
      'Your face loses architectural clarity — not from aging, but from nervous depletion and circulatory inefficiency.',
  },
  {
    id: 3,
    number: '03',
    name: 'The Overdriven Signal',
    tagline: 'Reactive Overload Pattern',
    description:
      'Your face reflects internal overdrive — fluctuating tone, asymmetry, reactive skin.',
  },
];

const MethodDetails = () => {
  const scrollToConsultationForm = () => {
    const form = document.getElementById('consultation-form');
    if (form) form.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="method-details" className="relative bg-warmWhite">
      <div className="relative py-12 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">

          {/* Header */}
          <div className="mb-8 sm:mb-16">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-[1px] bg-bronze"></div>
              <span className="text-bronze text-[9px] font-sans font-semibold tracking-[0.35em] uppercase">
                The Vionne Stability Method
              </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-4 lg:gap-20 items-end">
              <div>
                <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-light text-charcoal italic leading-[1.15] mb-2 sm:mb-4">
                  Where beauty reflects internal regulation,
                </h2>
                <p className="text-charcoal text-[12px] sm:text-[14px] font-sans font-semibold leading-relaxed">
                  and presence returns through systemic balance.
                </p>
              </div>
              <p className="hidden sm:block text-charcoal/70 text-[12px] sm:text-[13px] font-sans font-light leading-[1.9]">
                The Stability Method is not for everyone—and that is intentional. It is designed for women who are ready to address the root, not just the surface.
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-bronze/30 to-transparent mb-8 sm:mb-16"></div>

          {/* Intro Paragraph — desktop only */}
          <p className="hidden sm:block text-charcoal/80 text-[12px] sm:text-[13px] font-sans font-light leading-[1.9] max-w-3xl mb-12 sm:mb-16">
            The Vionne Stability™ Method approaches facial change through systemic balance rather than isolated correction. Instead of focusing solely on visible concerns such as lines, pores, or breakouts, the method examines the deeper regulatory patterns shaping the face—where nervous tone, circulation, inflammation, and tissue resilience intersect.
          </p>

          {/* Content blocks */}
          <div className="space-y-6 sm:space-y-10">

            {/* 3 Foundation Pillars */}
            <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-4 lg:gap-16">
              <div className="flex flex-row lg:flex-col items-center lg:items-start gap-3 lg:gap-1 pt-0.5">
                <span className="hidden lg:block font-display text-[2.8rem] font-light text-charcoal/[0.07] leading-none select-none">3</span>
                <span className="text-[10px] font-sans font-semibold text-bronze/70 tracking-[0.28em] uppercase">Foundation Pillars</span>
              </div>
              {/* Mobile: compact 3-item icon list */}
              <div className="grid grid-cols-1 gap-2 sm:hidden">
                {[
                  { icon: 'ri-ancient-gate-line', label: 'Traditional Chinese Medicine', sub: 'Meridian-based regulation' },
                  { icon: 'ri-plant-line', label: 'Botanical Therapy', sub: 'Plant-based skin support' },
                  { icon: 'ri-microscope-line', label: 'Modern Aesthetic Techniques', sub: 'Structure & facial harmony' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-bronze text-[14px]`}></i>
                    </div>
                    <div>
                      <span className="text-charcoal/80 text-[11px] font-sans font-medium">{item.label}</span>
                      <span className="text-charcoal/40 text-[10px] font-sans font-light ml-1">— {item.sub}</span>
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop: original */}
              <div className="hidden sm:block">
                <p className="text-charcoal/75 text-[12px] font-sans font-light leading-[1.9] mb-5">
                  The method is guided by three integrated disciplines:
                </p>
                <ul className="space-y-3 mb-5">
                  {[
                    { label: 'Traditional Chinese Medicine', desc: 'restoring internal balance through meridian-based regulation.' },
                    { label: 'Botanical Therapy', desc: 'using plant-based actives to support skin physiology and barrier resilience.' },
                    { label: 'Modern Aesthetic Techniques', desc: 'refined clinical methods designed to enhance structure and facial harmony.' },
                  ].map((item) => (
                    <li key={item.label} className="flex items-start gap-3">
                      <div className="w-1 h-1 rounded-full bg-bronze/50 mt-[7px] flex-shrink-0"></div>
                      <p className="text-charcoal/75 text-[12px] font-sans font-light leading-[1.9]">
                        <span className="font-medium text-charcoal">{item.label}</span>—{item.desc}
                      </p>
                    </li>
                  ))}
                </ul>
                <p className="text-charcoal/65 text-[11px] font-sans font-light leading-[1.85] italic">
                  Together, these pillars form a balanced approach that bridges classical medicine with contemporary aesthetic precision.
                </p>
              </div>
            </div>

            <div className="w-full h-[1px] bg-warmGray/20"></div>

            {/* 4 Regulatory Layers */}
            <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-4 lg:gap-16">
              <div className="flex flex-row lg:flex-col items-center lg:items-start gap-3 lg:gap-1 pt-0.5">
                <span className="hidden lg:block font-display text-[2.8rem] font-light text-charcoal/[0.07] leading-none select-none">4</span>
                <span className="text-[10px] font-sans font-semibold text-bronze/70 tracking-[0.28em] uppercase">Regulatory Layers</span>
              </div>
              {/* Mobile: compact 4-item icon list */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:hidden">
                {[
                  { icon: 'ri-pulse-line', label: 'Autonomic Nervous System' },
                  { icon: 'ri-compass-3-line', label: 'TCM Systemic Mapping' },
                  { icon: 'ri-leaf-line', label: 'Botanical Neuro-Modulation' },
                  { icon: 'ri-focus-3-line', label: 'Structural Reinforcement' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-bronze text-[13px]`}></i>
                    </div>
                    <span className="text-charcoal/75 text-[11px] font-sans font-light leading-snug">{item.label}</span>
                  </div>
                ))}
              </div>
              {/* Desktop: original */}
              <div className="hidden sm:block">
                <p className="text-charcoal/75 text-[12px] font-sans font-light leading-[1.9] mb-3">
                  Facial change rarely occurs at a single level.
                </p>
                <p className="text-charcoal/75 text-[12px] font-sans font-light leading-[1.9]">
                  Within the Stability framework, the face is observed through four interacting system layers. Understanding your unique body rhythm allows treatment to move beyond surface correction toward genuine stability.
                </p>
              </div>
            </div>

            <div className="w-full h-[1px] bg-warmGray/20"></div>

            {/* 5 Stability Facial Archetypes */}
            <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-4 lg:gap-16">
              <div className="flex flex-row lg:flex-col items-center lg:items-start gap-3 lg:gap-1 pt-0.5">
                <span className="hidden lg:block font-display text-[2.8rem] font-light text-charcoal/[0.07] leading-none select-none">5</span>
                <span className="text-[10px] font-sans font-semibold text-bronze/70 tracking-[0.28em] uppercase">Stability Facial Archetypes</span>
              </div>
              {/* Mobile: compact 3-item list */}
              <div className="grid grid-cols-1 gap-2 sm:hidden">
                {[
                  { icon: 'ri-shield-line', label: 'The Held Composure', sub: 'Chronic Tension Pattern' },
                  { icon: 'ri-drop-line', label: 'The Quiet Depletion', sub: 'Structural Fade Pattern' },
                  { icon: 'ri-pulse-line', label: 'The Overdriven Signal', sub: 'Reactive Overload Pattern' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-bronze text-[14px]`}></i>
                    </div>
                    <div>
                      <span className="text-charcoal/80 text-[11px] font-sans font-medium">{item.label}</span>
                      <span className="text-charcoal/40 text-[10px] font-sans font-light ml-1">— {item.sub}</span>
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop: original */}
              <div className="hidden sm:block">
                <p className="text-charcoal/75 text-[12px] font-sans font-light leading-[1.9]">
                  Over time, recurring facial states begin to appear among individuals navigating sustained mental and physical demands. These patterns are not skin types but regulatory signatures—subtle ways in which internal imbalance expresses itself through facial structure and tone. Within the Stability framework, these patterns are described through five facial archetypes, reflecting how tension, reactivity, circulation, barrier resilience, and structural vitality shape the face.&nbsp;The Stability Archetypes offer a guiding lens for understanding these patterns and restoring balance.
                </p>
              </div>
            </div>

            <div className="w-full h-[1px] bg-warmGray/20"></div>

            {/* From Regulation to Aesthetic Refinement */}
            <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-4 lg:gap-16">
              <div className="flex flex-row lg:flex-col items-center lg:items-start gap-3 lg:gap-1 pt-1">
                <span className="text-[10px] font-sans font-semibold text-bronze/70 tracking-[0.28em] uppercase leading-snug">From Regulation to Refinement</span>
              </div>
              {/* Mobile: compact quote block */}
              <div className="sm:hidden flex items-start gap-3 px-4 py-3 bg-bronze/[0.05] border-l-[2px] border-bronze/40">
                <i className="ri-focus-3-line text-bronze text-sm flex-shrink-0 mt-0.5"></i>
                <p className="text-charcoal/70 text-[11px] font-sans font-light leading-[1.8] italic">
                  Beauty, in this framework, is the visible expression of internal stability.
                </p>
              </div>
              {/* Desktop: original */}
              <div className="hidden sm:block">
                <p className="text-charcoal/75 text-[12px] font-sans font-light leading-[1.9] mb-4">
                  The Stability Method begins with systematic regulation—calming neural tension, restoring circulation, and rebalancing the skin environment.
                </p>
                <p className="text-charcoal/75 text-[12px] font-sans font-light leading-[1.9] mb-6">
                  Only once stability returns does aesthetic refinement take place, allowing contour, tone, and expression to evolve naturally rather than through force.
                </p>
                <div className="flex items-start gap-4 px-6 py-5 bg-bronze/[0.05] border-l-[3px] border-bronze/40">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-focus-3-line text-bronze text-sm"></i>
                  </div>
                  <p className="text-charcoal/70 text-[11px] font-sans font-light leading-[1.85] italic">
                    Beauty, in this framework, is the visible expression of internal stability.
                  </p>
                </div>
              </div>
            </div>

            <div className="w-full h-[1px] bg-warmGray/20"></div>

            {/* Clinical Observations */}
            <div id="clinical-observations" className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-4 lg:gap-16">
              <div className="flex flex-row lg:flex-col items-center lg:items-start gap-3 lg:gap-1 pt-0.5">
                <span className="text-[10px] font-sans font-semibold text-bronze/70 tracking-[0.28em] uppercase leading-snug">Clinical Observations</span>
              </div>
              <div>
                {/* Mobile: compact inline list */}
                <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 lg:hidden">
                  {[
                    { num: '01', label: 'Stress-Related Skin Conditions', icon: 'ri-mental-health-line' },
                    { num: '02', label: 'Hormonal Imbalance', icon: 'ri-drop-line' },
                    { num: '03', label: 'Chronic Dullness & Uneven Tone', icon: 'ri-contrast-2-line' },
                    { num: '04', label: 'Facial Tension & Structural Shift', icon: 'ri-body-scan-line' },
                    { num: '05', label: 'Sensitivity & Barrier Disruption', icon: 'ri-shield-line' },
                    { num: '06', label: 'Premature Ageing from Internal Load', icon: 'ri-time-line' },
                  ].map((item) => (
                    <div key={item.num} className="flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-bronze text-[13px]`}></i>
                      </div>
                      <span className="text-charcoal/75 text-[11px] font-sans font-light leading-snug">{item.label}</span>
                    </div>
                  ))}
                </div>
                {/* Desktop: original card grid */}
                <div className="hidden lg:grid grid-cols-6 gap-4">
                  {[
                    { num: '01', label: 'Stress-Related Skin Conditions', icon: 'ri-mental-health-line' },
                    { num: '02', label: 'Hormonal Imbalance', icon: 'ri-drop-line' },
                    { num: '03', label: 'Chronic Dullness & Uneven Tone', icon: 'ri-contrast-2-line' },
                    { num: '04', label: 'Facial Tension & Structural Shift', icon: 'ri-body-scan-line' },
                    { num: '05', label: 'Sensitivity & Barrier Disruption', icon: 'ri-shield-line' },
                    { num: '06', label: 'Premature Ageing from Internal Load', icon: 'ri-time-line' },
                  ].map((item) => (
                    <div key={item.num} className="flex flex-col items-center text-center gap-3 px-3 py-5 bg-stone-50 border border-warmGray/20 rounded-sm">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-bronze/[0.08] flex-shrink-0">
                        <i className={`${item.icon} text-bronze text-base`}></i>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-[8px] font-sans font-light text-bronze/50 tracking-widest">{item.num}</span>
                        <span className="text-charcoal/80 text-[11px] font-sans font-light leading-snug">{item.label}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="w-full h-[1px] bg-warmGray/20"></div>

            {/* What You May Notice */}
            <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-4 lg:gap-16">
              <div className="flex flex-row lg:flex-col items-center lg:items-start gap-3 lg:gap-1 pt-0.5">
                <span className="text-[10px] font-sans font-semibold text-bronze/70 tracking-[0.28em] uppercase leading-snug">After Your First Session</span>
              </div>
              <div>
                {/* Mobile: compact 2-col icon list */}
                <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 sm:hidden">
                  {[
                    { icon: 'ri-wind-line', tag: 'Jaw & Breath' },
                    { icon: 'ri-eye-line', tag: 'Eye Tone' },
                    { icon: 'ri-heart-pulse-line', tag: 'Internal Urgency' },
                    { icon: 'ri-body-scan-line', tag: 'Structural Shift' },
                    { icon: 'ri-drop-line', tag: 'Skin Response' },
                    { icon: 'ri-mental-health-line', tag: 'Presence & Clarity' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-bronze text-[13px]`}></i>
                      </div>
                      <span className="text-charcoal/75 text-[11px] font-sans font-light leading-snug">{item.tag}</span>
                    </div>
                  ))}
                </div>
                {/* Desktop: original card grid */}
                <div className="hidden sm:block">
                  <p className="text-charcoal/75 text-[12px] font-sans font-light leading-[1.9] mb-8">
                    Many clients report these shifts within the first session. Regulation is not always dramatic — but it is always felt.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                    {[
                      { icon: 'ri-wind-line', tag: 'Jaw & Breath', detail: 'A softened jaw and reduced facial holding — a steadier, deeper breath returns.' },
                      { icon: 'ri-eye-line', tag: 'Eye Tone', detail: 'Clearer eye tone and restored brightness, visible within the first session.' },
                      { icon: 'ri-heart-pulse-line', tag: 'Internal Urgency', detail: 'Less internal urgency. A calmer presence in how you move and speak.' },
                      { icon: 'ri-body-scan-line', tag: 'Structural Shift', detail: 'A measurable shift in facial symmetry and structural tone after regulation begins.' },
                      { icon: 'ri-drop-line', tag: 'Skin Response', detail: 'Reduced reactivity and improved skin tone as the nervous system settles.' },
                      { icon: 'ri-mental-health-line', tag: 'Presence & Clarity', detail: 'A quieter mind and more grounded sense of self — often noticed by others first.' },
                    ].map((item, i) => (
                      <div
                        key={i}
                        className="group relative flex items-start gap-4 px-5 py-5 bg-cream/40 border border-warmGray/25 hover:border-bronze/35 hover:bg-cream/70 hover:shadow-sm transition-all duration-300"
                      >
                        <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-bronze scale-y-0 group-hover:scale-y-100 transition-transform duration-300 origin-top"></div>
                        <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 border border-bronze/15 bg-white group-hover:bg-bronze/[0.06] transition-all duration-300">
                          <i className={`${item.icon} text-bronze text-sm`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-charcoal text-[11px] font-sans font-semibold tracking-wide mb-1.5">{item.tag}</p>
                          <p className="text-charcoal/55 text-[10.5px] font-sans font-light leading-[1.75]">{item.detail}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-start gap-4 px-6 py-5 bg-bronze/[0.04] border-l-[3px] border-bronze/40">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-sparkling-line text-bronze text-sm"></i>
                    </div>
                    <p className="text-charcoal/60 text-[11px] font-sans font-light leading-[1.85] italic">
                      These are not promised outcomes — they are commonly reported observations. Each body responds in its own time and sequence.
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default MethodDetails;